import { inject, Injectable } from '@angular/core';
import { HelperRoutingService } from './helper-routing.service';
import { Timespan } from '@fichaje/logic-dialogs-v2/components/logic-dialogs.interface';

type StringDayISO = `${number}-${number}-${number}`;
type Locale = `${string}-${string}`;

@Injectable({
  providedIn: 'root'
})
export class HelperTimeService {
  helpRouting = inject(HelperRoutingService)

  //? ######## LEGEND ########
  //? stringISO =      '2025-05-30 08:00:00.000'
  //? stringDayISO =   '2025-05-28'
  //? localePretty =   '28/02/2025' - EU, ES
  //? timePretty =     '13:59'
  //? timeSpanPretty = '36h 9m 37s'
  //? date =            javascript Date() object

  dateToLocalePretty(date:Date, locale:Locale='es-ES'){ //! NO respeta el DOBLE DIGIT ... buscar y remplazar por dateToLocalTimePretty()
    //? return pretty date locale to ptint on interface: 28/02/2025, user local timezone
    return date.toLocaleDateString(locale)
  }

  dateToStringDayISO(date:Date):string{
    //? recibe un objeto Date() y
    //? devuelve en formato ISO: '2025-05-28' basado en el timezone Madrid
    return date.toLocaleDateString(
      "fr-CA",
      { timeZone: "Europe/Madrid", year:"numeric", month: "2-digit", day:"2-digit" }
    )
    // new Date().toLocaleDateString("fr-CA", {timeZone: "Europe/Madrid", year:"numeric", month: "2-digit", day:"2-digit"})
  }

  stringISOToTimePretty(dateString: string | null, showSeconds:boolean = false): string {
    //? return Format Time = '13:55',
    if (!dateString) return '';
    const date = new Date(dateString);
    const formatTwoDigits = (n:number) => {return n < 10 ? '0' + n : n;}
    let build = date.getHours().toString() + ':' + formatTwoDigits(date.getMinutes())
    if(showSeconds) build = build + ':' + formatTwoDigits(date.getSeconds())
    return build;
  }

  dateToFormatTime(date: Date, locale:Locale='es-ES'): string {
    //? return Format Time = '13:55',
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  }

  secondsToTimeSpanPretty(seconds: number, showSeconds:boolean = true): string {
    //? return Format period of time = '36h 9m'
    //? para pasar minutos multiplicar por 60, y horas por 60*60.
    if (seconds === 0) return '0';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    let result = '';
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m `;
    if (remainingSeconds > 0 && showSeconds) result += `${remainingSeconds}s`;

    return result.trim(); // Elimina cualquier espacio en blanco sobrante al final
  }
  dateNowHoursPretty(){
    // retorna un string con la hora en este momento en formato '14:22'
    return this.stringISOToTimePretty(new Date().toISOString())
  }




  minutesToTimeSpanPretty(minutes: number): string {
    //! delete?  like secondsToTimeSpanPretty but from minutes
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      } else {
        return `${hours}h ${remainingMinutes}m`;
      }
    }
  }
  formatDateToHourMinute(dateStr: string, locale: string, timeZone: string): string {
    //! CON TIME ZONE!!!! aun sin usar, ver ejemplo al final del documento.
    const date = new Date(dateStr + 'Z'); // Ensures it's parsed as UTC
    const formatter = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone
    });

    const parts = formatter.formatToParts(date);
    const hour = parts.find(p => p.type === 'hour')?.value ?? '00';
    const minute = parts.find(p => p.type === 'minute')?.value ?? '00';

    return `${hour}h ${minute}m`;
  }


  ISO8601toLocalTimePretty(
    ISO:string|undefined,
    format:'h:mm'|'hh:mm'|'hh:mm:ss'|'yyyy-mm-dd'|'dd/mm/yyyy' = 'hh:mm'
  ):string|undefined{
    if(!ISO) return undefined
    const fechaString: string = ISO;
    // 1. Crear un objeto Date a partir del string
    const fecha: Date = new Date(fechaString);
    // 2. Obtener la hora y los minutos
    return this.dateToLocalTimePretty(fecha, format)
  }
  dateToLocalTimePretty(
    fecha: Date,
    format:'h:mm'|'hh:mm'|'hh:mm:ss'|'yyyy-mm-dd'|'dd/mm/yyyy' = 'hh:mm'
  ):string|undefined{
    if(!fecha) return undefined
    // 1. Obtener la hora y los minutos
    // .getHours() y .getMinutes() devuelven números.
    const year: number = fecha.getFullYear();
    const month: number = fecha.getMonth() + 1;
    const day: number = fecha.getDate();
    const horas: number = fecha.getHours();
    const minutos: number = fecha.getMinutes();
    const seconds: number = fecha.getSeconds()
    // 3. Formatear para que siempre tenga dos dígitos (ej: 9 -> "09")
    const monthFormateado: string =     month.toString().padStart(2, '0');
    const dayFormateado: string =       day.toString().padStart(2, '0');
    const horasFormateadas: string =    horas.toString().padStart(2, '0');
    const minutosFormateados: string =  minutos.toString().padStart(2, '0');
    const secondsFormateados: string =  seconds.toString().padStart(2, '0');
    // 4. Construir el string final

    if(format === 'yyyy-mm-dd')
      return `${year}-${monthFormateado}-${dayFormateado}`;
    if(format === 'dd/mm/yyyy')
      return `${dayFormateado}/${monthFormateado}/${year}`;
    if(format === 'h:mm')
      return `${horas}:${minutosFormateados}`;
    if(format === 'hh:mm')
      return `${horasFormateadas}:${minutosFormateados}`;
    if(format === 'hh:mm:ss')
      return `${horasFormateadas}:${minutosFormateados}:${secondsFormateados}`;

    return 'Bad format'
  }
  public dateLimitFromNow(secondsFromNow: number): Date {
    // funcion utilizada en reemplazo de un timer, te genera una hora en el futuro,
    // basada en segundos, y esta hora se puede utilizar como limite de un suceso.
    return new Date(Date.now() + secondsFromNow * 1000);
  }

  public compareByTimeSpan(
    compareDate:  Date|undefined,
    baseDate:     Date,// = new Date(),
    units:        'second' | 'minute' | 'hour' | 'day'  = 'second', //| 'month' | 'year'
  ): Timespan|undefined {
    // funcion que compara una fecha con otra basado en distintos periodos de tiempo,
    // por defecto, compara una Date con Date now basado en segundos.
    // el resultado puede ser unidades de periodo positivo, negativo o igualdad.
    if (!compareDate || !baseDate) return undefined;
    const d1 = new Date(compareDate);
    const d2 = new Date(baseDate);
    let milisecondsToTimeSpan = 1000;

    // 432000000/(24*60*60*1000)
    switch (units) {
      case 'second':
        d1.setMilliseconds(0);
        d2.setMilliseconds(0);
        // milisecondsToTimeSpan = 1000;
        break;
      case 'minute':
        d1.setSeconds(0, 0);
        d2.setSeconds(0, 0);
        milisecondsToTimeSpan = 60*1000;
        break;
      case 'hour':
        d1.setMinutes(0, 0, 0);
        d2.setMinutes(0, 0, 0);
        milisecondsToTimeSpan = 60*60*1000;
        break;
      case 'day':
        d1.setHours(0, 0, 0, 0);
        d2.setHours(0, 0, 0, 0);
        milisecondsToTimeSpan = 24*60*60*1000;
        break;
      default:
        return undefined;

      // case 'month':
      //   d1.setDate(1);
      //   d1.setHours(0, 0, 0, 0);
      //   d2.setDate(1);
      //   d2.setHours(0, 0, 0, 0);
      //   milisecondsToTimeSpan = 24*60*60*1000 * 30 //! error, pendiente considerar los dias que tiene el mes determinado!
      //   break;
      // case 'year':
      //   d1.setMonth(0, 1);
      //   d1.setHours(0, 0, 0, 0);
      //   d2.setMonth(0, 1);
      //   d2.setHours(0, 0, 0, 0);
      //   milisecondsToTimeSpan = 24*60*60*1000 * 365 //! error, pendiente a considerar los dias que tiene ese año!
      //   break;
    }

    // return (+d1 - +d2)/milisecondsToTimeSpan; //! actualmente devuelve siempre diferencia en segundos

    return {
      q:(+d1 - +d2)/milisecondsToTimeSpan,
      units
    }
  };

}

